import { useState, useCallback, useEffect, useContext } from "react";
import { DEBUG } from '../constants';

import { LanguageContext } from '../../context/LanguageContext';

import { updateProgression } from '../../utils/api/progressionAPI';
import { updateProgressionInde } from '../../utils/api/progressionIndeAPI';

const debug = DEBUG;


export default function useQuestionsManager(type, gameOrParcoursId) {

	// Utilisation du contexte de langue global
	const { language } = useContext(LanguageContext);

	const [questions, setQuestions] = useState([]);


	useEffect(() => {
		if (questions.length > 0) {
			localStorage.setItem(`questions_${type}_${gameOrParcoursId}`, JSON.stringify(questions));
			if (debug) console.log("💾 Questions sauvegardées dans localStorage :", questions);
		}
	}, [questions, type, gameOrParcoursId]);


	useEffect(() => {
		const saved = localStorage.getItem(`questions_${type}_${gameOrParcoursId}`);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				setQuestions(parsed);
				if (debug) console.log("Questions restaurées depuis localStorage :", parsed);
			} catch (e) {
				console.error("Erreur de parsing des questions dans localStorage", e);
			}
		}
	}, [type, gameOrParcoursId]);


	const initializeQuestions = useCallback((rawQuestions) => {
		const initialized = rawQuestions.map((q) => ({
			...q,
			achieved: q.achieved ?? false,
			startTime: q.startTime ?? null,
			duration: q.duration ?? 0,
			nbCompass: q.nbCompass ?? 3,
			aborted: false,
		}));

		setQuestions(initialized);
		localStorage.setItem(`questions_${type}_${gameOrParcoursId}`, JSON.stringify(initialized));
	}, [type, gameOrParcoursId]);


	const setQuestionAchieved = useCallback( async (questionId, { duration, nbCompass, aborted }) => {

		// 1) MAJ locale optimiste
		setQuestions(prev => 
		prev.map(q =>
			q.id === questionId
			? { ...q, achieved: true, duration, nbCompass, aborted }
			: q
		)
		);
		
		// 2) Enregistrement serveur
		
		const userId = parseInt(localStorage.getItem('userId'));

		try {
			if (type === 'inde') {
				const progressionId = parseInt(localStorage.getItem(`progression_${type}_${gameOrParcoursId}`));
				await updateProgressionInde(progressionId, userId, gameOrParcoursId, questionId);
				// si tu as une API score distincte, appelle-la ici:
				// await saveScore({ progressionId, questionId, duration, nbCompass, aborted });
			} else if (type === 'villages') {
				const progressionId = parseInt(localStorage.getItem(`progression_${gameOrParcoursId}`));
				await updateProgression(progressionId, userId, gameOrParcoursId, questionId);
				// si tu as une API score distincte, appelle-la ici:
				// await saveScore({ progressionId, questionId, duration, nbCompass, aborted });
			}
			
		} catch (e) {
			console.error('Erreur updateProgression:', e);
			// option: rollback local si nécessaire
			// setQuestions(prev => prev.map(... remettre achieved: false ...))
			}
	}, []);


    const startQuestionTimer = useCallback((questionId) => {
        setQuestions(prev =>
        prev.map(q =>
            q.id === questionId
            ? { ...q, startTime: new Date().toLocaleString('sv-SE') }
            : q
        )
        );
    }, []);


	const updateQuestionField = useCallback((questionId, field, value) => {
		setQuestions(prevQuestions => {
			const updated = prevQuestions.map(q =>
				q.id === questionId
				? { ...q, [field]: value }
				: q
			);

			// Sauvegarde automatique
			localStorage.setItem(`questions_${type}_${gameOrParcoursId}`, JSON.stringify(updated));

			if (debug) console.log("✅ Questions mises à jour :", updated);

			return updated;
		});
	}, [type, language, gameOrParcoursId]);


	const saveQuestionsToStorage = useCallback(() => {
		localStorage.setItem(`questions_${type}_${gameOrParcoursId}`, JSON.stringify(questions));
	}, [type, language, gameOrParcoursId, questions]);


	// Analyse / Tests en cours...
	const loadFromStorage = useCallback(() => {
		const saved = localStorage.getItem(`questions_${type}_${gameOrParcoursId}`);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				setQuestions(parsed);
				if (debug) console.log("📥 Questions restaurées via loadFromStorage :", parsed);
				return true;
			} catch (e) {
				if (debug) console.error("Erreur de parsing dans loadFromStorage", e);
				return false;
			}
		} else { if (debug) console.warn("Aucune donnée trouvée pour loadFromStorage"); }

		return false;
	}, [type, language, gameOrParcoursId]);


	const clearQuestions = useCallback(() => {
		setQuestions([]); // Vide en mémoire
		
		localStorage.removeItem(`questions_${type}_${gameOrParcoursId}`); // Supprime du stockage local
		localStorage.removeItem(`repLibres_${type}_${gameOrParcoursId}`);
		localStorage.removeItem(`repQcm_${type}_${gameOrParcoursId}`);
		localStorage.removeItem(`gameLang-${gameOrParcoursId}`);

		if (debug) console.log(`🗑️ Données de questions supprimées pour le parcours ${gameOrParcoursId}`);

	}, [type, language, gameOrParcoursId]);

	return {
		questions,
		initializeQuestions,
		setQuestionAchieved,
		startQuestionTimer,
		updateQuestionField,
		saveQuestionsToStorage,
		loadFromStorage,
		clearQuestions,
		setQuestions, // facultatif si tu veux accès direct aussi
	};
}
